<script setup lang="ts">

import { AppTable, AppTd, AppTh, AppTr } from '@shared/ui/tables'
import { AppText } from '@shared/ui/typography'
import { LinkTextPrimary } from '@shared/ui/links'
import { BadgeBookingStatus } from '@shared/ui/badges'

import { BadgeAttendanceReason } from './badge-attendance-reason'
import { useTableTutorAttendanceRates } from '../model/table-tutor-attendance-rates.hook'

defineOptions({
  name: 'TableTutorAttendanceRates',
})

const COLUMNS: { label: string, isNowrap?: boolean }[] = [
  { label: 'Lesson #' },
  { label: 'Date & time', isNowrap: true },
  { label: 'Tutor' },
  { label: 'Customer' },
  { label: 'Kid' },
  { label: 'Lesson status' },
  { label: 'Reason' },
  { label: 'Score' },
]

const {
  rates,
  isLoading,
  hasRates,
  meta,
  perPageOptions,
  totals,
  emptyCell,
  getLessonUrl,
  getStart,
  getTutorName,
  getTutorUrl,
  getCustomerName,
  getCustomerUrl,
  getKidUrl,
  getScore,
  setChangePage,
  setChangePerPage,
} = useTableTutorAttendanceRates()
</script>

<template>
  <div class="table-tutor-attendance-rates">
    <AppTable
      class="rounded-table"
      :meta="meta"
      :per-page-options="perPageOptions"
      :loading="isLoading"
      @change:page="setChangePage"
      @change:per-page="setChangePerPage"
    >
      <template
        v-if="totals"
        #summary
      >
        <div class="table-tutor-attendance-rates__summary">
          <AppText
            name="body2"
            color="slate-700"
          >
            Max score: {{ totals.maxScore }}
          </AppText>
          <AppText
            name="body2"
            color="slate-700"
          >
            Actual score: {{ totals.actualScore }}
          </AppText>
        </div>
      </template>

      <template #head>
        <AppTr>
          <AppTh
            v-for="column in COLUMNS"
            :key="column.label"
            :class="{ 'table-tutor-attendance-rates__nowrap': column.isNowrap }"
          >
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              {{ column.label }}
            </AppText>
          </AppTh>
        </AppTr>
      </template>

      <template
        v-if="hasRates"
        #body
      >
        <AppTr
          v-for="rate in rates"
          :key="rate.id"
        >
          <AppTd class="medium">
            <LinkTextPrimary
              :href="getLessonUrl(rate.booking)"
              :aria-label="`Lesson ${rate.booking.id}`"
            >
              {{ rate.booking.id }}
            </LinkTextPrimary>
          </AppTd>
          <AppTd class="medium table-tutor-attendance-rates__nowrap">
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ getStart(rate) }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <LinkTextPrimary :href="getTutorUrl(rate.tutor.id)">
              {{ getTutorName(rate.tutor) }}
            </LinkTextPrimary>
          </AppTd>
          <AppTd class="medium">
            <LinkTextPrimary
              v-if="rate.customer"
              :href="getCustomerUrl(rate.customer.id)"
            >
              {{ getCustomerName(rate.customer) }}
            </LinkTextPrimary>
            <AppText
              v-else
              name="body1"
              color="slate-900"
            >
              {{ emptyCell }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <LinkTextPrimary
              v-if="rate.kid"
              :href="getKidUrl(rate.kid.id)"
            >
              {{ rate.kid.name }}
            </LinkTextPrimary>
            <AppText
              v-else
              name="body1"
              color="slate-900"
            >
              {{ emptyCell }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <BadgeBookingStatus
              :status="rate.booking.status"
              :category="rate.booking.reason_category ?? undefined"
            />
          </AppTd>
          <AppTd class="medium">
            <BadgeAttendanceReason :reason="rate.reason" />
          </AppTd>
          <AppTd class="medium">
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ getScore(rate) }}
            </AppText>
          </AppTd>
        </AppTr>
      </template>
    </AppTable>
  </div>
</template>

<style scoped lang="scss" src="./TableTutorAttendanceRates.scss" />
